/* eslint-disable no-underscore-dangle */
import { getPPartId } from '@parallel-fluidics/shared/models/projects/parts';
import { formatDate } from '@parallel-fluidics/shared/shipping';
import { toCanvas } from 'bwip-js';

// Label geometry for the Brother QL series on 62mm continuous media: the
// printable width is 696px at 300dpi, and we draw a portrait label — name,
// lot code and part fields stacked top-down with the QR at the bottom.
// 1240px ≈ 105mm long fits the worst case (2-line name + 2-line material)
// without crowding the QR. The Pi print server accepts either orientation —
// pixels print 1:1, so this layout is the exact label.
const LABEL_W = 696;
const LABEL_H = 1240;
const MARGIN = 36;
// ~50mm QR so laptop webcams can scan it from a shelf.
const QR_SIZE = 590;

// Roboto is the app-wide Google Fonts face (public/index.html), so every
// machine draws the label from the same font files — no OS/browser has a
// system Roboto to shadow it. Availability is verified before drawing.
const FONT_FAMILY = 'Roboto, sans-serif';
const font = (weight, size) => `${weight} ${size}px ${FONT_FAMILY}`;

/**
 * Render the QR into a canvas at (close to) QR_SIZE. bwip-js sizes by integer
 * scale (px per module), not target width — so measure at scale 1, then
 * re-render at the largest scale that fits, keeping modules pixel-crisp
 * instead of resampling.
 */
function renderQrCanvas(url) {
  const qrCanvas = document.createElement('canvas');
  toCanvas(qrCanvas, {
    bcid: 'qrcode', text: url, eclevel: 'M', scale: 1,
  });
  const scale = Math.max(1, Math.floor(QR_SIZE / qrCanvas.width));
  toCanvas(qrCanvas, {
    bcid: 'qrcode', text: url, eclevel: 'M', scale,
  });
  return qrCanvas;
}

/** "jane.smith@..." → "JS", "hao@..." → "HAO" (single-word local parts stay whole, capped). */
function initialsFromEmail(email) {
  const segments = (email || '').split('@')[0].split(/[^a-zA-Z]+/).filter(Boolean);
  if (!segments.length) return '';
  if (segments.length === 1) return segments[0].slice(0, 3).toUpperCase();
  return segments.map((segment) => segment[0]).join('').toUpperCase();
}

/**
 * Firestore timestamp → "YYYY-MM-DD" in the factory calendar
 * (America/New_York, via the shared shipping formatDate), matching the
 * lot code's date — slicing toISOString here would stamp tomorrow's UTC
 * date on evening prints.
 */
function labelDate(timestamp) {
  const date = timestamp?.toDate?.();
  return date ? formatDate(date) : null;
}

/**
 * Set the largest font (from `size` down to `minSize`) at which `text` fits
 * `maxWidth`, then ellipsis-truncate if it still overflows. Returns the text
 * to draw; the fitted font is left set on the context. Never lets canvas
 * squish glyphs via fillText's maxWidth — that makes labels unreadable.
 */
/* eslint-disable no-param-reassign */
function fitText(ctx, text, maxWidth, { size, weight, minSize = size }) {
  let fontSize = size;
  ctx.font = font(weight, fontSize);
  while (fontSize > minSize && ctx.measureText(text).width > maxWidth) {
    fontSize -= 1;
    ctx.font = font(weight, fontSize);
  }
  if (ctx.measureText(text).width <= maxWidth) return text;
  let truncated = text;
  while (truncated.length > 1 && ctx.measureText(`${truncated}…`).width > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return `${truncated}…`;
}
/* eslint-enable no-param-reassign */

/** Ellipsis-truncate one line to maxWidth using the context's current font. */
function truncateLine(ctx, line, maxWidth) {
  if (ctx.measureText(line).width <= maxWidth) return line;
  let out = line;
  while (out.length > 1 && ctx.measureText(`${out}…`).width > maxWidth) {
    out = out.slice(0, -1);
  }
  return `${out}…`;
}

/** Split one over-wide token into column-width chunks (CSS break-word). */
function breakToken(ctx, word, maxWidth) {
  const chunks = [];
  let current = '';
  [...word].forEach((char) => {
    const candidate = current + char;
    if (!current || ctx.measureText(candidate).width <= maxWidth) {
      current = candidate;
    } else {
      chunks.push(current);
      current = char;
    }
  });
  if (current) chunks.push(current);
  return chunks;
}

/**
 * Greedy word-wrap into at most maxLines lines at the context's current
 * font. A token wider than the column breaks across lines instead of
 * overflowing (or losing its middle to an ellipsis); only overflow past
 * the last line is ellipsis-truncated.
 */
function wrapText(ctx, text, maxWidth, maxLines) {
  const words = String(text).split(/\s+/).filter(Boolean)
    .flatMap((word) => (ctx.measureText(word).width <= maxWidth
      ? [word]
      : breakToken(ctx, word, maxWidth)));
  const lines = [];
  let current = '';
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (!current || ctx.measureText(candidate).width <= maxWidth) {
      current = candidate;
    } else {
      lines.push(current);
      current = word;
    }
  });
  if (current) lines.push(current);
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = truncateLine(
      ctx,
      [kept[maxLines - 1], ...lines.slice(maxLines)].join(' '),
      maxWidth,
    );
    return kept;
  }
  return lines;
}

/**
 * Map a lot and its part/project into the exact strings the renderer draws —
 * all composition happens here, renderLotLabel just draws values. The part
 * contributes two separate lines: its display name ("Anti-backflow
 * reservoir") and its full id ("P069-0069-A-COC"). MADE always comes from
 * the lot's creator (not the current user), so reprints produce an
 * identical label.
 */
export function buildLotLabelFields({ lot, part, projectName }) {
  const fullPartId = [projectName, part ? getPPartId(part) : null].filter(Boolean).join('-');
  return {
    code: lot.code,
    // Deploys always set REACT_APP_ORIGIN; the fallback keeps a missing
    // env from ever printing "undefined/lots/..." QR codes.
    url: `${process.env.REACT_APP_ORIGIN || window.location.origin}/lots/${lot.id}`,
    name: part?.name || null,
    partId: fullPartId || null,
    material: lot.material,
    qty: lot.initialQty != null ? String(lot.initialQty) : null,
    made: [
      labelDate(lot._createdBy?.timestamp),
      initialsFromEmail(lot._createdBy?.email),
    ].filter(Boolean).join(' · '),
  };
}

/**
 * Render a lot label to a base64 PNG (no data-url prefix) for the Pi print
 * server: PARALLEL / COMPONENT LOT header over a thick rule, then stacked
 * top-down — part display name, the lot code large, the part/material/qty/
 * made field rows — with the QR and its scan hint pinned to the bottom.
 * The status is deliberately never printed — it changes over the lot's
 * life; the label doesn't.
 */
export default async function renderLotLabel({
  code, url, name, partId, material, qty, made,
}) {
  // The Roboto stylesheet loads at app start, but weight files fetch
  // lazily on first use — 900 is label-only, so this can be a real
  // network fetch on a machine's first print. check() catches a failed
  // fetch: a visible error beats silently printing whatever fallback
  // font that machine happens to have.
  const weights = [400, 700, 900];
  await Promise.all(weights.map((weight) => document.fonts.load(font(weight, 32))));
  const missing = weights.filter((weight) => !document.fonts.check(font(weight, 32)));
  if (missing.length) {
    throw new Error(
      `Label font failed to load (Roboto ${missing.join(', ')}) — check the network and retry`,
    );
  }

  const canvas = document.createElement('canvas');
  canvas.width = LABEL_W;
  canvas.height = LABEL_H;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  const contentWidth = LABEL_W - 2 * MARGIN;

  // Header: brand left, label type right, over a thick rule.
  if ('letterSpacing' in ctx) ctx.letterSpacing = '5px';
  ctx.font = font(900, 34);
  ctx.fillText('PARALLEL', MARGIN, MARGIN);
  if ('letterSpacing' in ctx) ctx.letterSpacing = '3px';
  ctx.font = font(700, 23);
  ctx.textAlign = 'right';
  ctx.fillText('COMPONENT LOT', MARGIN + contentWidth, MARGIN + 10);
  ctx.textAlign = 'left';
  if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';

  const ruleY = MARGIN + 46;
  ctx.fillRect(MARGIN, ruleY, contentWidth, 6);
  let y = ruleY + 24;

  // Part display name, prominent — wraps to two lines, then truncates.
  ctx.font = font(700, 40);
  const nameLines = wrapText(ctx, name || '—', contentWidth, 2);
  nameLines.forEach((line, index) => {
    ctx.fillText(line, MARGIN, y + index * 48);
  });
  y += nameLines.length * 48 + 16;

  // Lot code, large. Shrinks (never truncates) if a code ever outgrows the width.
  const codeText = fitText(ctx, code, contentWidth, { size: 56, weight: 900, minSize: 36 });
  ctx.fillText(codeText, MARGIN, y);
  y += 56 + 20;

  // Field rows: uppercase label column, value beside it. Material word-wraps
  // (the black resin grades are ~90 chars) and only then ellipsis-truncates;
  // part ids are short, so PART keeps one line to protect the QR's space.
  const LABEL_COL = 185;
  const VALUE_LINE = 46;
  const rows = [
    ['PART', partId, 1],
    ['MATERIAL', material, 2],
    ['QTY', qty, 1],
    ['MADE', made, 1],
  ];
  rows.forEach(([label, value, maxLines]) => {
    ctx.font = font(700, 26);
    ctx.fillText(label, MARGIN, y + 6);
    ctx.font = font(400, 36);
    const lines = wrapText(ctx, value || '—', contentWidth - LABEL_COL, maxLines);
    lines.forEach((line, index) => {
      ctx.fillText(line, MARGIN + LABEL_COL, y + index * VALUE_LINE);
    });
    y += lines.length * VALUE_LINE + 12;
  });

  // QR pinned to the bottom, centered, with the scan hint centered under it.
  const qrCanvas = renderQrCanvas(url);
  const hintY = LABEL_H - MARGIN - 22;
  const qrY = hintY - 12 - qrCanvas.height;
  ctx.drawImage(qrCanvas, (LABEL_W - qrCanvas.width) / 2, qrY);
  ctx.font = font(400, 22);
  ctx.textAlign = 'center';
  ctx.fillText('Scan for status, inspection & history', LABEL_W / 2, hintY);
  ctx.textAlign = 'left';

  return canvas.toDataURL('image/png').replace(/^data:image\/png;base64,/, '');
}
